export { default as clsx } from 'clsx';
import { createMemoStyles } from '@wds/styles';
export { darken, lighten } from 'polished';
import { isValidElement, useRef, useEffect, useLayoutEffect } from 'react';
import isElement from 'lodash.iselement';
export { default as isElement } from 'lodash.iselement';
import { createPortal } from 'react-dom';
import { jsx } from 'react/jsx-runtime';

var clearfix = {
  '&:after': {
    content: '',
    display: 'block',
    clear: 'both'
  }
};

/**
 * @description
 * This factory function creates a hook that will return the wrenchSpacing class to be used in the component
 * Simply import it and call at the component level to create the hook, then use the hook inside your component
 * Code example: https://github.com/mntv-web-experience/wrench/blob/main/packages/box/Box
 */
function createSpacingStyles() {
  return createMemoStyles(function (_a) {
    var spacing = _a.spacing;
    return {
      wrenchSpacing: function (_a) {
        var _b, _c, _d, _e, _f, _g, _h, _j;
        var m = _a.m,
          mx = _a.mx,
          my = _a.my,
          mt = _a.mt,
          mb = _a.mb,
          ml = _a.ml,
          mr = _a.mr,
          p = _a.p,
          px = _a.px,
          py = _a.py,
          pt = _a.pt,
          pb = _a.pb,
          pl = _a.pl,
          pr = _a.pr;
        var margin = {
          t: (_b = m !== null && m !== void 0 ? m : my) !== null && _b !== void 0 ? _b : mt,
          b: (_c = m !== null && m !== void 0 ? m : my) !== null && _c !== void 0 ? _c : mb,
          l: (_d = m !== null && m !== void 0 ? m : mx) !== null && _d !== void 0 ? _d : ml,
          r: (_e = m !== null && m !== void 0 ? m : mx) !== null && _e !== void 0 ? _e : mr
        };
        var padding = {
          t: (_f = p !== null && p !== void 0 ? p : py) !== null && _f !== void 0 ? _f : pt,
          b: (_g = p !== null && p !== void 0 ? p : py) !== null && _g !== void 0 ? _g : pb,
          l: (_h = p !== null && p !== void 0 ? p : px) !== null && _h !== void 0 ? _h : pl,
          r: (_j = p !== null && p !== void 0 ? p : px) !== null && _j !== void 0 ? _j : pr
        };
        return {
          marginTop: margin.t && spacing[margin.t],
          marginRight: margin.r && spacing[margin.r],
          marginBottom: margin.b && spacing[margin.b],
          marginLeft: margin.l && spacing[margin.l],
          paddingTop: padding.t && spacing[padding.t],
          paddingRight: padding.r && spacing[padding.r],
          paddingBottom: padding.b && spacing[padding.b],
          paddingLeft: padding.l && spacing[padding.l]
        };
      }
    };
  });
}

/* eslint-disable no-bitwise  */
/* Given a hex or rgb color value, checks if the contrasting color should be light or dark */
var getContrastColor = function getContrastColor(color) {
  var _a;
  var r = 0;
  var g = 0;
  var b = 0;
  // Check the format of the color, HEX or RGB?
  if (color.match(/^rgb/)) {
    var colorValues = (_a = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/)) !== null && _a !== void 0 ? _a : ['', '', ''];
    r = parseInt(colorValues[1], 10);
    g = parseInt(colorValues[2], 10);
    b = parseInt(colorValues[3], 10);
  } else {
    var colorReg = color.length < 5 ? /./g : '';
    var colorValues = +"0x".concat(color.slice(1).replace(colorReg, '$&$&'));
    r = colorValues >> 16;
    g = colorValues >> 8 & 255;
    b = colorValues & 255;
  }
  // HSP equation from http://alienryderflex.com/hsp.html
  var hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
  if (hsp > 160) {
    return 'dark';
  }
  return 'light';
};

function getLineTruncationProperties(truncate) {
  if (truncate !== undefined && truncate > 0) {
    return {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whitespace: truncate === 1 ? 'nowrap' : undefined,
      display: '-webkit-box',
      boxOrient: 'vertical',
      lineClamp: truncate
    };
  }
  return undefined;
}

// https://snook.ca/archives/html_and_css/hiding-content-for-accessibility
var defaultBaseline = {
  boxSizing: 'border-box',
  padding: 0,
  margin: 0,
  listStyle: 'none'
};

var defaultFonts = function defaultFonts(_a) {
  var type = _a.type;
  return {
    fontFamily: type.fontFamily.base,
    fontSize: type.fontSize.body,
    '-webkit-font-smoothing': 'antialiased'
  };
};

function deprecate(props, key, value, componentName) {
  if (props[key] === value) {
    // eslint-disable-next-line no-console
    console.warn("[DEPRECATED]: The ".concat(componentName, " prop ").concat(String(key
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    ), " with value \"").concat(value, "\" is deprecated and will be removed in the future"));
  }
}

// eslint-disable-next-line @typescript-eslint/ban-types
function extractType(node) {
  if ( /*#__PURE__*/isValidElement(node)) return node.type;
  return '';
}
function extractDisplayName(type) {
  var _a, _b;
  return (_b = (_a = type.displayName) !== null && _a !== void 0 ? _a : type.name) !== null && _b !== void 0 ? _b : 'Unknown';
}
var getDisplayName = function getDisplayName(child) {
  var type = extractType(child);
  if (typeof type === 'function' || typeof type === 'object') {
    return extractDisplayName(type);
  }
  if (typeof type === 'symbol') {
    // eslint-disable-next-line @typescript-eslint/ban-types
    return type.toString();
  }
  return type;
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var getState = function getState(state, newState) {
  return __assign(__assign({}, state), newState);
};

var isControlled = function isControlled(value) {
  return !(value === undefined || value === null);
};

var isBrowser = typeof window !== 'undefined';

var guardElement = function guardElement(e) {
  return isElement(e);
};

/**
 * focusable function, taken from jQuery UI Core
 * https://github.com/marklagendijk/jquery.tabbable/blob/master/jquery.tabbable.js
 */
function isFormControlElement(element) {
  return /^(input|select|textarea|button)$/.test(element.nodeName.toLowerCase());
}
var isFocusableElement = function isFocusableElement(element) {
  var map;
  var mapName;
  var img;
  var nodeName = element.nodeName.toLowerCase();
  var tabIndexAttr = element.getAttribute('tabindex');
  var tabIndex = tabIndexAttr !== null ? parseInt(tabIndexAttr, 10) : NaN;
  var isTabIndexNotNaN = !Number.isNaN(tabIndex);
  if (nodeName === 'area') {
    map = element.parentNode;
    mapName = map.name;
    if (!element.href || !mapName || map.nodeName.toLowerCase() !== 'map') {
      return false;
    }
    img = document.querySelector("img[usemap=\"#".concat(mapName, "\"]"));
    return !!img;
  }
  if (isFormControlElement(element)) {
    return !element.disabled;
  }
  if (nodeName === 'a') {
    return element.href && element.href !== '' || isTabIndexNotNaN;
  }
  return isTabIndexNotNaN || tabIndex <= -1;
};

function removeUndefinedProperties(obj) {
  var result = {};
  Object.entries(obj).forEach(function (_a) {
    var key = _a[0],
      value = _a[1];
    if (value !== undefined) {
      result[key] = value;
    }
  });
  return result;
}

var safeRest = function safeRest(_a) {
  _a.className;
    _a.style;
    var props = __rest(_a, ["className", "style"]);
  return props;
};

var conditionGuard = function conditionGuard(what, conditional) {
  return conditional;
};
var notEmpty = function notEmpty(value) {
  return value !== null && value !== undefined;
};
var handlePortal = function handlePortal(content, renderTo) {
  if (!renderTo) return content;
  if (conditionGuard(renderTo, isElement(renderTo))) return /*#__PURE__*/createPortal(content, renderTo);
  if (renderTo.current) return /*#__PURE__*/createPortal(content, renderTo.current);
  return content;
};

// Taken from lodash: https://github.com/lodash/lodash/blob/master/uniqueId.js
var idCounter = 0;
var uniqueId = function uniqueId(prefix) {
  var id = ++idCounter;
  if (prefix == null) {
    throw new Error('The value is missing in uniqueId() parameter');
  }
  return "".concat(prefix).concat(id);
};

// send in the state value as a parameter and get the previous value of that state
var usePreviousState = function usePreviousState(value) {
  var ref = useRef();
  useEffect(function () {
    ref.current = value;
  });
  return ref.current;
};

/**
 * SSR Safe version of `useLayoutEffect`
 *
 * falls back to `useEffect` when not run in a browser
 */
var useIsomorphicLayoutEffect = isBrowser ? useLayoutEffect : useEffect;

// https://snook.ca/archives/html_and_css/hiding-content-for-accessibility
var visuallyHidden = {
  border: '0px',
  clip: 'rect(1px, 1px, 1px, 1px)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  width: '1px',
  // https://medium.com/@jessebeach/beware-smushed-off-screen-accessible-text-5952a4c2cbfe
  whiteSpace: 'nowrap',
  wordWrap: 'normal'
};

var withNested = function withNested(component, args) {
  return Object.assign(component, args);
};

function withSpacing(Component) {
  /**
   * JSS injects css styles in the order of `createUseStyles` usage
   * Hence, useStyles needs to be instantiated every single time while
   * using this HOC because it should be the last `createUseStyles` usage
   * for a component. With this mechanism, `withSpacing` overwrites the component
   * spacing. Not ideal for a design system, but we are waiting on the
   * below ticket.
   *
   * Note: https://jira.surveymonkey.com/browse/WRENCH-1107
   *
   * If the decision is have `withStyles` NOT overwrite the component spacing,
   * just move this declaration outside of this component. `withSpacing` will then
   * add to the styles and not overwrite them.
   */
  var useStyles = createMemoStyles(function (_a) {
    var spacing = _a.spacing;
    return {
      wrenchSpacing: function (_a) {
        var margin = _a.margin,
          padding = _a.padding;
        return {
          marginTop: margin.t && spacing[margin.t],
          marginRight: margin.r && spacing[margin.r],
          marginBottom: margin.b && spacing[margin.b],
          marginLeft: margin.l && spacing[margin.l],
          paddingTop: padding.t && spacing[padding.t],
          paddingRight: padding.r && spacing[padding.r],
          paddingBottom: padding.b && spacing[padding.b],
          paddingLeft: padding.l && spacing[padding.l]
        };
      }
    };
  });
  var WithSpacing = function WithSpacing(_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j;
    var m = _a.m,
      mx = _a.mx,
      my = _a.my,
      mt = _a.mt,
      mb = _a.mb,
      ml = _a.ml,
      mr = _a.mr,
      p = _a.p,
      px = _a.px,
      py = _a.py,
      pt = _a.pt,
      pb = _a.pb,
      pl = _a.pl,
      pr = _a.pr,
      className = _a.className,
      rest = __rest(_a, ["m", "mx", "my", "mt", "mb", "ml", "mr", "p", "px", "py", "pt", "pb", "pl", "pr", "className"]);
    var margin = {
      t: (_b = m !== null && m !== void 0 ? m : my) !== null && _b !== void 0 ? _b : mt,
      b: (_c = m !== null && m !== void 0 ? m : my) !== null && _c !== void 0 ? _c : mb,
      l: (_d = m !== null && m !== void 0 ? m : mx) !== null && _d !== void 0 ? _d : ml,
      r: (_e = m !== null && m !== void 0 ? m : mx) !== null && _e !== void 0 ? _e : mr
    };
    var padding = {
      t: (_f = p !== null && p !== void 0 ? p : py) !== null && _f !== void 0 ? _f : pt,
      b: (_g = p !== null && p !== void 0 ? p : py) !== null && _g !== void 0 ? _g : pb,
      l: (_h = p !== null && p !== void 0 ? p : px) !== null && _h !== void 0 ? _h : pl,
      r: (_j = p !== null && p !== void 0 ? p : px) !== null && _j !== void 0 ? _j : pr
    };
    var classes = useStyles({
      margin: margin,
      padding: padding
    });
    var Element = Component;
    return jsx(Element, __assign({
      spacingClass: classes.wrenchSpacing,
      className: className
    }, safeRest(rest)));
  };
  WithSpacing.displayName = "WithSpacing(".concat(getDisplayName(Component), ")");
  WithSpacing.getOriginalComponent = function () {
    return Component;
  };
  return WithSpacing;
}

var KEYS = {
  DOWN: 'ArrowDown',
  END: 'End',
  ESC: 'Escape',
  HOME: 'Home',
  LEFT: 'ArrowLeft',
  PAGE_DOWN: 'PageDown',
  PAGE_UP: 'PageUp',
  RIGHT: 'ArrowRight',
  UP: 'ArrowUp',
  ENTER: 'Enter',
  SPACE: ' ',
  BACKSPACE: 'Backspace',
  TAB: 'Tab'
};

export { KEYS, clearfix, conditionGuard, createSpacingStyles, defaultBaseline, defaultFonts, deprecate, extractDisplayName, extractType, getContrastColor, getDisplayName, getLineTruncationProperties, getState, guardElement, handlePortal, isBrowser, isControlled, isFocusableElement, notEmpty, removeUndefinedProperties, safeRest, uniqueId, useIsomorphicLayoutEffect, usePreviousState, visuallyHidden, withNested, withSpacing };
